import type { Span } from './ast'

export type TokenKind =
  | 'eof'
  | 'number'
  | 'string'
  | 'identifier'
  | 'bracket_ident'
  | 'lparen'
  | 'rparen'
  | 'lbrace'
  | 'rbrace'
  | 'comma'
  | 'dot'
  | 'colon'
  | 'op'
  | 'keyword'

export interface Token {
  kind: TokenKind
  text: string
  span: Span
}

const KEYWORDS = new Set([
  'if',
  'then',
  'elseif',
  'else',
  'end',
  'case',
  'when',
  'and',
  'or',
  'not',
  'in',
  'true',
  'false',
  'null',
  'fixed',
  'include',
  'exclude',
])

function isAlpha(c: string): boolean {
  return /[A-Za-z_]/.test(c)
}

function isAlnum(c: string): boolean {
  return /[A-Za-z0-9_]/.test(c)
}

function isDigit(c: string): boolean {
  return /[0-9]/.test(c)
}

export function lexTableauCalc(input: string): { tokens: Token[] } {
  const s = String(input ?? '')
  const tokens: Token[] = []
  let i = 0

  const push = (kind: TokenKind, text: string, start: number, end: number) => {
    tokens.push({ kind, text, span: { start, end } })
  }

  const skipWhitespaceAndComments = () => {
    while (i < s.length) {
      const c = s[i]
      // whitespace
      if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
        i++
        continue
      }
      // line comment: //
      if (c === '/' && s[i + 1] === '/') {
        i += 2
        while (i < s.length && s[i] !== '\n') i++
        continue
      }
      // line comment: --
      if (c === '-' && s[i + 1] === '-') {
        i += 2
        while (i < s.length && s[i] !== '\n') i++
        continue
      }
      // block comment: /* ... */
      if (c === '/' && s[i + 1] === '*') {
        i += 2
        while (i < s.length) {
          if (s[i] === '*' && s[i + 1] === '/') {
            i += 2
            break
          }
          i++
        }
        continue
      }
      break
    }
  }

  while (true) {
    skipWhitespaceAndComments()
    if (i >= s.length) break

    const start = i
    const c = s[i]

    // Bracket identifier: [Field Name] or [Source]
    if (c === '[') {
      i++ // skip '['
      let buf = ''
      while (i < s.length) {
        const ch = s[i]
        if (ch === ']' && s[i + 1] === ']') {
          // escaped closing bracket
          buf += ']'
          i += 2
          continue
        }
        if (ch === ']') {
          i++
          break
        }
        buf += ch
        i++
      }
      push('bracket_ident', buf, start, i)
      continue
    }

    // String literal: '...'
    if (c === "'") {
      i++
      let buf = ''
      while (i < s.length) {
        const ch = s[i]
        if (ch === "'" && s[i + 1] === "'") {
          buf += "'"
          i += 2
          continue
        }
        if (ch === "'") {
          i++
          break
        }
        buf += ch
        i++
      }
      push('string', buf, start, i)
      continue
    }

    // String literal: "..."
    if (c === '"') {
      i++
      let buf = ''
      while (i < s.length) {
        const ch = s[i]
        if (ch === '"' && s[i + 1] === '"') {
          buf += '"'
          i += 2
          continue
        }
        if (ch === '"') {
          i++
          break
        }
        buf += ch
        i++
      }
      push('string', buf, start, i)
      continue
    }

    // Number literal: 123, 1.23, .5 (but not lone '.')
    if (isDigit(c) || (c === '.' && isDigit(s[i + 1] || ''))) {
      let seenDot = false
      if (c === '.') {
        seenDot = true
        i++
      }
      while (i < s.length) {
        const ch = s[i]
        if (isDigit(ch)) {
          i++
          continue
        }
        if (ch === '.' && !seenDot) {
          seenDot = true
          i++
          continue
        }
        break
      }
      push('number', s.slice(start, i), start, i)
      continue
    }

    // Identifiers / keywords
    if (isAlpha(c)) {
      i++
      while (i < s.length && isAlnum(s[i])) i++
      const raw = s.slice(start, i)
      const lower = raw.toLowerCase()
      if (KEYWORDS.has(lower)) {
        push('keyword', lower, start, i)
      } else {
        push('identifier', raw, start, i)
      }
      continue
    }

    // Punctuation
    if (c === '(') {
      i++
      push('lparen', '(', start, i)
      continue
    }
    if (c === ')') {
      i++
      push('rparen', ')', start, i)
      continue
    }
    if (c === '{') {
      i++
      push('lbrace', '{', start, i)
      continue
    }
    if (c === '}') {
      i++
      push('rbrace', '}', start, i)
      continue
    }
    if (c === ',') {
      i++
      push('comma', ',', start, i)
      continue
    }
    if (c === '.') {
      i++
      push('dot', '.', start, i)
      continue
    }
    if (c === ':') {
      i++
      push('colon', ':', start, i)
      continue
    }

    // Operators (longest-first)
    const two = s.slice(i, i + 2)
    const three = s.slice(i, i + 3)
    if (three === '<>=' || three === '!==') {
      // not standard Tableau; treat as op anyway
      i += 3
      push('op', three, start, i)
      continue
    }
    if (two === '<=' || two === '>=' || two === '!=' || two === '<>') {
      i += 2
      push('op', two, start, i)
      continue
    }
    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^' || c === '=' || c === '<' || c === '>') {
      i++
      push('op', c, start, i)
      continue
    }

    // Unknown character: emit as op token so parser can surface it.
    i++
    push('op', c, start, i)
  }

  push('eof', '', s.length, s.length)
  return { tokens }
}

